import React, { useRef, useEffect, useState } from 'react';
import './ImageEditor.css';

const TOOLS = {
  BRUSH: 'brush',
  RECTANGLE: 'rectangle',
  ELLIPSE: 'ellipse',
  TEXT: 'text',
};

const COLORS = [
  '#FF0000', // 红色
  '#FFFF00', // 黄色
  '#0000FF', // 蓝色
  '#FFFFFF', // 白色
  '#000000', // 黑色
];

function ImageEditor({ imageUrl, onSave, onCancel, isUploading, uploadProgress = 0 }) {
  const canvasRef = useRef(null);
  const tempCanvasRef = useRef(null);
  const baseImageRef = useRef(null);
  const originalImageRef = useRef(null); // 保存原始图片（不包含任何绘制）
  const historyRef = useRef([]); // 保存操作历史
  const textsRef = useRef([]); // 保存所有文字对象
  const draggedTextRef = useRef(null); // 当前被拖动的文字
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // 拖动偏移量
  const [tool, setTool] = useState(TOOLS.BRUSH);
  const [color, setColor] = useState('#FF0000'); // 默认红色
  const [brushSize, setBrushSize] = useState(5);
  const [textSize, setTextSize] = useState(24); // 文字大小
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const safeUploadProgress = Number.isFinite(Number(uploadProgress))
    ? Math.max(0, Math.min(100, Math.round(Number(uploadProgress))))
    : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;

    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    // 只有非 blob URL 才需要设置 crossOrigin
    if (!imageUrl.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      // 设置 canvas 尺寸
      const maxWidth = 1200;
      const maxHeight = 800;
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // 保存原始图片
      baseImageRef.current = img;
      originalImageRef.current = img;

      // 绘制图片
      ctx.drawImage(img, 0, 0, width, height);
      setImageLoaded(true);
      
      // 清空文字列表
      textsRef.current = [];
      
      // 保存初始状态到历史（包含空的文字列表）
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const imageData = canvas.toDataURL();
          historyRef.current.push({ imageData, texts: [] });
          setCanUndo(false);
        }
      }, 100);
    };

    img.onerror = () => {
      console.error('Failed to load image');
      setImageLoaded(false);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    if (!imageLoaded) return;
    const pos = getCanvasCoordinates(e);
    
    if (tool === TOOLS.TEXT) {
      // 检查是否点击在已有文字上
      const textHit = getTextAtPosition(pos.x, pos.y);
      if (textHit) {
        // 开始拖动文字
        draggedTextRef.current = textHit.text;
        dragOffsetRef.current = {
          x: pos.x - textHit.text.x,
          y: pos.y - textHit.text.y
        };
        setIsDrawing(true);
      } else {
        // 添加新文字位置
        setTextPosition(pos);
        setIsDrawing(false);
        // 聚焦到文字输入框
        setTimeout(() => {
          const textInput = document.querySelector('.text-input');
          if (textInput) textInput.focus();
        }, 100);
      }
    } else {
      setIsDrawing(true);
      setStartPos(pos);
    }
  };

  const saveStateToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imageData = canvas.toDataURL();
    // 同时保存文字列表的副本
    const textsCopy = JSON.parse(JSON.stringify(textsRef.current));
    historyRef.current.push({ imageData, texts: textsCopy });
    // 限制历史记录数量，避免内存占用过大
    if (historyRef.current.length > 20) {
      historyRef.current.shift();
    }
    setCanUndo(historyRef.current.length > 1);
  };

  const handleUndo = () => {
    if (historyRef.current.length <= 1) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 移除当前状态
    historyRef.current.pop();
    
    // 恢复到上一个状态
    const prevState = historyRef.current[historyRef.current.length - 1];
    const imageData = typeof prevState === 'string' ? prevState : (prevState.imageData || prevState);
    const prevTexts = typeof prevState === 'object' && prevState.texts ? prevState.texts : [];
    
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // 恢复文字列表
      textsRef.current = JSON.parse(JSON.stringify(prevTexts));
      // 重新绘制所有文字
      textsRef.current.forEach(text => {
        ctx.fillStyle = text.color;
        ctx.font = `${text.size}px Arial`;
        ctx.fillText(text.content, text.x, text.y);
      });
      
      // 更新 baseImageRef
      const updatedImg = new Image();
      updatedImg.onload = () => {
        baseImageRef.current = updatedImg;
      };
      updatedImg.src = canvas.toDataURL();
      
      setCanUndo(historyRef.current.length > 1);
    };
    img.src = imageData;
  };

  const redrawCanvas = (useOriginal = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const sourceImage = useOriginal ? originalImageRef.current : baseImageRef.current;
    if (!sourceImage) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);
    
    // 重新绘制所有文字
    textsRef.current.forEach(text => {
      ctx.fillStyle = text.color;
      ctx.font = `${text.size}px Arial`;
      ctx.fillText(text.content, text.x, text.y);
    });
  };

  const getTextAtPosition = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    
    // 从后往前检查，后添加的文字在上层
    for (let i = textsRef.current.length - 1; i >= 0; i--) {
      const text = textsRef.current[i];
      ctx.font = `${text.size}px Arial`;
      const metrics = ctx.measureText(text.content);
      const textWidth = metrics.width;
      const textHeight = text.size;
      
      // 检查点击位置是否在文字范围内
      if (x >= text.x && x <= text.x + textWidth && 
          y >= text.y - textHeight && y <= text.y) {
        return { text, index: i };
      }
    }
    return null;
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentPos = getCanvasCoordinates(e);

    if (tool === TOOLS.TEXT && draggedTextRef.current) {
      // 拖动文字 - 使用 baseImageRef（包含之前的绘制），避免丢失画笔/形状
      draggedTextRef.current.x = currentPos.x - dragOffsetRef.current.x;
      draggedTextRef.current.y = currentPos.y - dragOffsetRef.current.y;
      redrawCanvas(false); // 使用 baseImageRef，包含之前的绘制
    } else if (tool === TOOLS.BRUSH) {
      if (startPos) {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(currentPos.x, currentPos.y);
        ctx.strokeStyle = color;
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        setStartPos(currentPos);
      }
    } else if (tool === TOOLS.RECTANGLE || tool === TOOLS.ELLIPSE) {
      // 重绘基础图片，然后绘制当前形状预览
      redrawCanvas(false);
      drawShape(ctx, startPos, currentPos, tool, color, brushSize);
    }
  };

  const drawShape = (ctx, start, end, shapeType, strokeColor, lineWidth) => {
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();

    const width = end.x - start.x;
    const height = end.y - start.y;

    if (shapeType === TOOLS.RECTANGLE) {
      ctx.rect(start.x, start.y, width, height);
    } else if (shapeType === TOOLS.ELLIPSE) {
      const centerX = start.x + width / 2;
      const centerY = start.y + height / 2;
      const radiusX = Math.abs(width / 2);
      const radiusY = Math.abs(height / 2);
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
    }

    ctx.stroke();
  };

  const handleMouseUp = (e) => {
    if (!isDrawing || !imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentPos = getCanvasCoordinates(e);

    if (tool === TOOLS.TEXT && draggedTextRef.current) {
      // 文字拖动完成，更新基础图片（不包含文字）
      // 先保存文字，临时移除文字，保存 baseImage，再恢复文字
      const textsBackup = JSON.parse(JSON.stringify(textsRef.current));
      textsRef.current = [];
      
      // 重新绘制（不包含文字）以获取干净的 baseImage
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (baseImageRef.current) {
        ctx.drawImage(baseImageRef.current, 0, 0, canvas.width, canvas.height);
      }
      
      const img = new Image();
      img.onload = () => {
        baseImageRef.current = img;
        // 恢复文字并重新绘制完整内容
        textsRef.current = textsBackup;
        redrawCanvas(false);
        saveStateToHistory();
      };
      img.src = canvas.toDataURL();
      draggedTextRef.current = null;
    } else if (tool === TOOLS.RECTANGLE || tool === TOOLS.ELLIPSE) {
      // 最终绘制形状
      drawShape(ctx, startPos, currentPos, tool, color, brushSize);
      // 更新基础图片为当前 canvas 内容（包含形状，但不包含文字）
      // 先保存文字，临时移除文字，保存 baseImage，再恢复文字
      const textsBackup = JSON.parse(JSON.stringify(textsRef.current));
      textsRef.current = [];
      
      // 重新绘制（不包含文字）以获取干净的 baseImage
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (baseImageRef.current) {
        ctx.drawImage(baseImageRef.current, 0, 0, canvas.width, canvas.height);
      }
      drawShape(ctx, startPos, currentPos, tool, color, brushSize);
      
      const img = new Image();
      img.onload = () => {
        baseImageRef.current = img;
        // 恢复文字并重新绘制完整内容
        textsRef.current = textsBackup;
        redrawCanvas(false);
        // 保存状态到历史
        saveStateToHistory();
      };
      img.src = canvas.toDataURL();
    } else if (tool === TOOLS.BRUSH) {
      // 画笔绘制完成后，更新 baseImage（不包含文字）
      // 先保存文字，临时移除文字，保存 baseImage，再恢复文字
      const textsBackup = JSON.parse(JSON.stringify(textsRef.current));
      textsRef.current = [];
      
      const img = new Image();
      img.onload = () => {
        baseImageRef.current = img;
        // 恢复文字并重新绘制完整内容
        textsRef.current = textsBackup;
        redrawCanvas(false);
        // 保存状态到历史
        saveStateToHistory();
      };
      img.src = canvas.toDataURL();
    }

    setIsDrawing(false);
    setStartPos(null);
  };

  const handleTextAdd = () => {
    if (!textInput.trim() || !textPosition) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // 创建文字对象
    const textObj = {
      content: textInput,
      x: textPosition.x,
      y: textPosition.y,
      color: color,
      size: textSize
    };
    
    // 添加到文字列表
    textsRef.current.push(textObj);
    
    // 重新绘制所有内容（使用 baseImageRef，它包含之前的画笔/形状绘制，但不包含文字）
    redrawCanvas(false);
    
    // 不更新 baseImageRef，因为它应该只包含原始图片和画笔/形状绘制，不包含文字
    // 文字始终从 textsRef.current 中绘制
    // 保存状态到历史
    saveStateToHistory();
    
    setTextInput('');
    setTextPosition(null);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob && onSave) {
        onSave(blob);
      }
    }, 'image/png');
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    if (!imageUrl.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      baseImageRef.current = img;
      originalImageRef.current = img;
      
      // 清空文字列表
      textsRef.current = [];
      
      // 清空历史记录，只保留当前清除后的状态
      historyRef.current = [];
      // 保存清除后的状态作为新的初始状态
      saveStateToHistory();
      setCanUndo(false);
    };
    img.src = imageUrl;
  };

  return (
    <div className="image-editor">
      <div className="image-editor-toolbar">
        <div className="tool-group">
          <button
            className={`tool-btn ${tool === TOOLS.BRUSH ? 'active' : ''}`}
            onClick={() => setTool(TOOLS.BRUSH)}
            title="画笔"
          >
            ✏️
          </button>
          <button
            className={`tool-btn ${tool === TOOLS.RECTANGLE ? 'active' : ''}`}
            onClick={() => setTool(TOOLS.RECTANGLE)}
            title="矩形"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="14" height="14" rx="3" stroke="#1976d2" strokeWidth="2.2" fill="#e3f2fd" />
            </svg>
          </button>
          <button
            className={`tool-btn ${tool === TOOLS.ELLIPSE ? 'active' : ''}`}
            onClick={() => setTool(TOOLS.ELLIPSE)}
            title="椭圆"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="11" cy="11" rx="7" ry="7" stroke="#1976d2" strokeWidth="2.2" fill="#e3f2fd" />
            </svg>
          </button>
          <button
            className={`tool-btn ${tool === TOOLS.TEXT ? 'active' : ''}`}
            onClick={() => setTool(TOOLS.TEXT)}
            title="文字"
          >
            A
          </button>
        </div>

        <div className="tool-group">
          <label className="color-picker-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2px solid #888',
              background: 'linear-gradient(135deg, #FF0000 0%, #FFFF00 25%, #00FF00 50%, #0000FF 75%, #FF00FF 100%)',
              boxShadow: '0 0 4px #aaa',
              marginRight: 4
            }} title="选择颜色">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" stroke="#fff" strokeWidth="2" fill="none" />
                <circle cx="10" cy="10" r="5" fill={color} />
              </svg>
            </span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="color-picker"
              style={{
                width: 28,
                height: 28,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                outline: 'none',
                position: 'relative',
                zIndex: 2,
              }}
              title="选择颜色"
            />
          </label>
          <div className="color-palette">
            {COLORS.map((c) => (
              <button
                key={c}
                className={`color-btn ${color === c ? 'active' : ''}`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
                title={c}
              />
            ))}
          </div>
        </div>

        <div className="tool-group">
          <label className="brush-size-label">
            粗细:
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="brush-size-slider"
            />
            <span>{brushSize}px</span>
          </label>
        </div>

        {tool === TOOLS.TEXT && (
          <>
            <div className="tool-group">
              <label className="brush-size-label">
                文字大小:
                <input
                  type="range"
                  min="12"
                  max="72"
                  value={textSize}
                  onChange={(e) => setTextSize(Number(e.target.value))}
                  className="brush-size-slider"
                />
                <span>{textSize}px</span>
              </label>
            </div>
            <div className="text-input-group">
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={textPosition ? "输入文字后点击添加" : "点击画布选择位置"}
                className="text-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && textPosition) {
                    handleTextAdd();
                  }
                }}
              />
              {textPosition && (
                <button onClick={handleTextAdd} className="text-add-btn">
                  添加
                </button>
              )}
            </div>
          </>
        )}

        <div className="tool-group actions">
          <button 
            onClick={handleUndo} 
            className="action-btn undo-btn"
            disabled={!canUndo || isUploading}
            title="撤销"
          >
            ↶ 回退
          </button>
          <button onClick={handleClear} className="action-btn clear-btn">
            🗑️ 清除
          </button>
          <button
            onClick={handleSave}
            className="action-btn save-btn"
            disabled={isUploading || !imageLoaded}
          >
            {isUploading ? `⏳ 上传中... ${safeUploadProgress}%` : '✓ 确定'}
          </button>
          <button onClick={onCancel} className="action-btn cancel-btn" disabled={isUploading}>
            ✕ 取消
          </button>
        </div>
      </div>

      <div className="image-editor-canvas-container" style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={(e) => {
            handleMouseMove(e);
            // 在文字工具下，如果鼠标在文字上，改变光标样式
            if (tool === TOOLS.TEXT && imageLoaded) {
              const pos = getCanvasCoordinates(e);
              const textHit = getTextAtPosition(pos.x, pos.y);
              const canvas = canvasRef.current;
              if (canvas) {
                canvas.style.cursor = textHit ? 'move' : 'crosshair';
              }
            }
          }}
          onMouseUp={handleMouseUp}
          onMouseLeave={(e) => {
            handleMouseUp(e);
            const canvas = canvasRef.current;
            if (canvas) {
              canvas.style.cursor = tool === TOOLS.TEXT ? 'crosshair' : 'crosshair';
            }
          }}
          className="image-editor-canvas"
          style={{ cursor: tool === TOOLS.TEXT ? 'crosshair' : 'crosshair' }}
        />
        {!imageLoaded && (
          <div className="image-editor-loading">加载图片中...</div>
        )}
        {isUploading && (
          <div
            className="image-editor-uploading-overlay"
          >
            <div className="image-editor-uploading-card">
              <div className="image-editor-uploading-title">图片上传中…</div>
              <div className="image-editor-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={safeUploadProgress}>
                <div
                  className="image-editor-progress-fill"
                  style={{
                    width: `${safeUploadProgress}%`,
                    minWidth: safeUploadProgress > 0 ? 8 : 0,
                  }}
                />
              </div>
              <div className="image-editor-uploading-meta">
                <span>已上传</span>
                <span className="image-editor-uploading-percent">{safeUploadProgress}%</span>
              </div>
              <div className="image-editor-uploading-hint">请勿关闭窗口</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageEditor;

